/**
 * HmSaleNew — wystawienie dokumentu sprzedaży w Symfonii.
 *
 * Jedyny ekran modułu, który ZMIENIA cudzy system. Stąd trzy decyzje, które wyglądają na
 * nadmiarowe, a nie są:
 *
 *  • DWA PRZYCISKI, nie jeden. „Do bufora" tworzy dokument niezatwierdzony (można go jeszcze
 *    poprawić w Symfonii), „Wystaw" nadaje numer i zatwierdza — tego nie cofniemy przez WebAPI.
 *    Zatwierdzenie prosi o potwierdzenie, bufor nie.
 *  • KSeF jest wyborem, nie domyślną wartością. Domyślnie „bez KSeF", bo wysyłka do KSeF jest
 *    nieodwracalna i ma skutki prawne; wybór wariantu wysyłkowego dokłada ostrzeżenie do
 *    potwierdzenia.
 *  • SUMY SĄ PODGLĄDOWE. Liczymy je w przeglądarce, żeby było widać, co się składa, ale kwoty
 *    autorytatywne wylicza Symfonia przy zapisie (rabaty, zaokrąglenia, typ ceny) i to jej
 *    odpowiedź pokazujemy po wystawieniu.
 *
 * Zależności między polami są wymuszone przez Symfonię, nie przez nas: serie zależą od TYPU
 * dokumentu, a formy płatności od REJESTRU. Dlatego oba pola dociągają się po wyborze nadrzędnego
 * i czyszczą się przy jego zmianie — inaczej wysłalibyśmy parę, którą Symfonia odrzuci.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  HmAdHocBuyer,
  HmContractorListItem,
  HmKsefKind,
  HmListResponse,
  HmProductListItem,
  HmSaleDictionaries,
  HmSaleIssueInput,
  HmSaleIssueResult,
} from '@saas/shared';
import { HM_KSEF_KINDS, HM_PRICE_KINDS } from '@saas/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Plus, Save, Send, Trash2, UserPlus } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DatePicker } from '@/components/DatePicker';
import { AdHocBuyerModal } from '@/components/handel-magazyn/AdHocBuyerModal';
import { computeSaleTotals } from '@/components/handel-magazyn/sale-totals';
import { Input } from '@/components/Input';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Select } from '@/components/Select';
import { Textarea } from '@/components/Textarea';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { useActiveCompanyScope } from '@/lib/use-active-company';

/** Wiersz pozycji w formularzu: użytkownik podaje CENĘ, backend przelicza ją na wartość. */
interface PositionRow {
  key: string;
  productCode: string;
  name: string;
  quantity: string;
  unitPrice: string;
  unit: string;
  vatRateId: string;
  /**
   * `true` = cena podstawiona z cennika, `false` = wpisana ręcznie.
   *
   * Rozróżnienie jest potrzebne przy ZMIANIE CENNIKA: ceny podstawione przeliczamy na nowy
   * cennik, a wpisane ręcznie zostawiamy. Bez tej flagi zmiana cennika albo kasowałaby pracę
   * użytkownika, albo zostawiała ceny ze starego cennika — obie opcje po cichu.
   */
  priceFromCatalog: boolean;
}

function emptyRow(): PositionRow {
  return {
    key: crypto.randomUUID(),
    productCode: '',
    name: '',
    quantity: '1',
    unitPrice: '0',
    unit: '',
    vatRateId: '',
    priceFromCatalog: false,
  };
}

/** Dzisiejsza data z pól LOKALNYCH — `toISOString()` w strefie na wschód od UTC cofa o dobę. */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function num(value: string): number {
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function money(value: number): string {
  return (Math.round(value * 100) / 100).toFixed(2);
}

export function HmSaleNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const companyId = useActiveCompanyScope().companyId;

  const [documentTypeId, setDocumentTypeId] = useState('');
  const [series, setSeries] = useState('');
  const [departmentCode, setDepartmentCode] = useState('');
  const [registryId, setRegistryId] = useState('');
  const [paymentFormId, setPaymentFormId] = useState('');
  const [salePriceType, setSalePriceType] = useState('');
  // NETTO domyślnie (kod 2). UWAGA: 1 to BRUTTO — patrz `HM_PRICE_KINDS`; odwrotne przypisanie
  // nie daje błędu, tylko dokument na inną kwotę.
  const [priceKind, setPriceKind] = useState(String(HM_PRICE_KINDS.NET));
  const [buyerId, setBuyerId] = useState<string | null>(null);
  /**
   * Nabywca jednokrotny — wyklucza się z wyborem z kartoteki, więc trzymamy go osobno
   * i czyścimy jedno przy ustawieniu drugiego. Symfonia odrzuca oba naraz.
   */
  const [adHocBuyer, setAdHocBuyer] = useState<HmAdHocBuyer | null>(null);
  const [adHocOpen, setAdHocOpen] = useState(false);
  const [issueDate, setIssueDate] = useState(today());
  const [saleDate, setSaleDate] = useState(today());
  const [maturityDate, setMaturityDate] = useState('');
  const [description, setDescription] = useState('');
  const [note, setNote] = useState('');
  const [ksefKind, setKsefKind] = useState<string>(String(HM_KSEF_KINDS.NONE));
  const [rows, setRows] = useState<PositionRow[]>([emptyRow()]);
  const [confirmIssue, setConfirmIssue] = useState(false);

  const q = `companyId=${companyId}`;

  const dictionaries = useQuery({
    queryKey: ['handel-magazyn', 'sale-dictionaries', companyId],
    enabled: !!companyId,
    retry: false,
    queryFn: async () =>
      (await api.get<HmSaleDictionaries>(`/handel-magazyn/sale-dictionaries?${q}`)).data,
  });

  const contractors = useQuery({
    queryKey: ['handel-magazyn', 'contractors', companyId],
    enabled: !!companyId,
    retry: false,
    queryFn: async () =>
      (await api.get<HmListResponse<HmContractorListItem>>(`/handel-magazyn/contractors?${q}`)).data
        .items,
  });

  const products = useQuery({
    queryKey: ['handel-magazyn', 'products', companyId],
    enabled: !!companyId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<HmListResponse<HmProductListItem>>(
          // Ceny cenników są w osobnym zasobie Symfonii — prosimy o nie jawnie.
          `/handel-magazyn/products?withPrices=true&${q}`,
        )
      ).data.items,
  });

  // Serie należą do KONKRETNEGO typu dokumentu — bez tego wyboru nie ma o co pytać.
  const seriesQuery = useQuery({
    queryKey: ['handel-magazyn', 'document-series', companyId, documentTypeId],
    enabled: !!companyId && !!documentTypeId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<{ items: { id: number; code: string }[] }>(
          `/handel-magazyn/document-series?documentTypeId=${documentTypeId}&${q}`,
        )
      ).data.items,
  });

  // Formy płatności są ZAWĘŻONE DO REJESTRU — pytamy dopiero po jego wyborze.
  const formsQuery = useQuery({
    queryKey: ['handel-magazyn', 'payment-forms', companyId, registryId],
    enabled: !!companyId && !!registryId,
    retry: false,
    queryFn: async () =>
      (
        await api.get<{ items: { id: number; name: string; term: number | null }[] }>(
          `/handel-magazyn/payment-forms?registryId=${registryId}&${q}`,
        )
      ).data.items,
  });

  const dict = dictionaries.data;

  // Podpowiedzi startowe: pierwszy dział i pierwszy aktywny cennik. Nie zgadujemy typu dokumentu
  // ani rejestru — to wybory, które użytkownik musi zrobić świadomie.
  useEffect(() => {
    if (!dict) return;
    setDepartmentCode((prev) => prev || (dict.departments[0]?.code ?? ''));
    setSalePriceType((prev) => prev || String(dict.salePriceTypes[0]?.type ?? ''));
  }, [dict]);

  // Zmiana typu dokumentu unieważnia serię — seria z innego typu jest odrzucana przy zapisie.
  useEffect(() => {
    setSeries('');
  }, [documentTypeId]);

  useEffect(() => {
    const items = seriesQuery.data;
    if (items?.length) setSeries((prev) => prev || (items[0]?.code ?? ''));
  }, [seriesQuery.data]);

  // Zmiana rejestru unieważnia formę płatności — z tego samego powodu.
  useEffect(() => {
    setPaymentFormId('');
  }, [registryId]);

  useEffect(() => {
    const forms = formsQuery.data;
    if (!forms?.length) return;
    const registry = dict?.paymentRegistries.find((r) => String(r.id) === registryId);
    const preferred = forms.find((f) => f.id === registry?.defaultPaymentFormId) ?? forms[0];
    setPaymentFormId((prev) => prev || String(preferred?.id ?? ''));
  }, [formsQuery.data, dict, registryId]);

  const totals = useMemo(
    () =>
      computeSaleTotals(
        rows.map((r) => ({
          quantity: num(r.quantity),
          unitPrice: num(r.unitPrice),
          vatPercent: dict?.vatRates.find((v) => String(v.id) === r.vatRateId)?.percent ?? null,
        })),
        Number(priceKind),
      ),
    [rows, dict, priceKind],
  );

  function setRow(key: string, patch: Partial<PositionRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  /**
   * Cena towaru z WYBRANEGO cennika. Zwraca `null`, gdy towar nie ma ceny w tym cenniku —
   * wtedy nie podstawiamy zera, bo zero to konkretna cena, a nie „brak ceny".
   */
  function catalogPrice(product: HmProductListItem, priceTypeValue: string) {
    const type = Number(priceTypeValue);
    if (!Number.isFinite(type)) return null;
    return product.prices.find((price) => price.priceType === type) ?? null;
  }

  /**
   * Wybór towaru podstawia jednostkę, domyślną stawkę VAT i CENĘ Z CENNIKA wybranego na
   * dokumencie („typ A", „typ B"…). Cena z cennika niesie też swój rodzaj (brutto/netto),
   * więc ustawiamy go razem z nią — inaczej kwota znaczyłaby co innego niż w cenniku.
   */
  function pickProduct(key: string, code: string | null) {
    const product = products.data?.find((p) => p.code === code);
    if (!product) {
      setRow(key, { productCode: '' });
      return;
    }
    // Kod stawki na towarze („23%") i w słowniku (bywa " 23%") różnią się białymi znakami —
    // backend przycina słownik, więc tutaj wystarczy przyciąć wartość z towaru.
    const rate = dict?.vatRates.find((v) => v.code === (product.vatRate ?? '').trim());
    const price = catalogPrice(product, salePriceType);
    if (price && price.kind && String(price.kind) !== priceKind) setPriceKind(String(price.kind));

    setRow(key, {
      productCode: product.code,
      name: product.name,
      unit: product.unit ?? '',
      vatRateId: rate ? String(rate.id) : '',
      ...(price ? { unitPrice: price.value, priceFromCatalog: true } : {}),
    });
  }

  /**
   * Czyści formularz po wystawieniu dokumentu.
   *
   * Potrzebne, bo powłoka kart trzyma karty PRZY ŻYCIU — po wystawieniu i powrocie na „Nowy
   * dokument" zobaczyłbyś poprzednią treść i mógłbyś ją wystawić drugi raz, nie zauważywszy.
   *
   * Czyścimy TREŚĆ dokumentu (nabywca, pozycje, opis, notatka, daty), a ZOSTAWIAMY ustawienia
   * wystawiania: typ dokumentu, serię, dział, rejestr, formę płatności, cennik i wariant KSeF.
   * To stałe stanowiska pracy — kto wystawia faktury seryjnie, wybierałby je od nowa przy każdym
   * dokumencie. Tak samo zachowuje się formatka w Symfonii.
   */
  function resetDocument() {
    setBuyerId(null);
    setAdHocBuyer(null);
    setRows([emptyRow()]);
    setDescription('');
    setNote('');
    setIssueDate(today());
    setSaleDate(today());
    setMaturityDate('');
  }

  const mutation = useMutation({
    mutationFn: async (issue: boolean) => {
      const payload: HmSaleIssueInput = {
        documentTypeCode:
          dict?.documentTypes.find((d) => String(d.id) === documentTypeId)?.code ?? '',
        series,
        departmentCode,
        paymentRegistryId: Number(registryId),
        paymentFormId: Number(paymentFormId),
        ...(salePriceType ? { salePriceType: Number(salePriceType) } : {}),
        ...(priceKind ? { priceKind: Number(priceKind) } : {}),
        ...(issueDate ? { issueDate } : {}),
        ...(saleDate ? { saleDate } : {}),
        ...(maturityDate ? { maturityDate } : {}),
        // Jedna droga albo druga — nigdy obie (walidator odrzuci komplet).
        ...(adHocBuyer ? { buyerAdHoc: adHocBuyer } : buyerId ? { buyerId: Number(buyerId) } : {}),
        ...(description ? { description } : {}),
        ...(note ? { note } : {}),
        positions: rows.map((r) => ({
          productCode: r.productCode,
          quantity: num(r.quantity),
          unitPrice: num(r.unitPrice),
          ...(r.unit ? { unitOfMeasurement: r.unit } : {}),
          ...(r.name ? { name: r.name } : {}),
          ...(r.vatRateId ? { vatRateId: Number(r.vatRateId) } : {}),
        })),
        issue,
        ksefKind: Number(ksefKind) as HmKsefKind,
      };
      return (await api.post<HmSaleIssueResult>(`/handel-magazyn/sales?${q}`, payload)).data;
    },
    onSuccess: (doc) => {
      toast.show({
        variant: 'success',
        title: doc.inBuffer
          ? t('tenant.handelMagazyn.newSale.toast.buffered', { number: doc.documentNumber })
          : t('tenant.handelMagazyn.newSale.toast.issued', { number: doc.documentNumber }),
      });
      // Najpierw czyścimy, potem przechodzimy do dokumentu: karta „Nowy dokument" zostaje
      // otwarta w tle i po powrocie ma być pusta, a nie z treścią właśnie wystawionej faktury.
      resetDocument();
      navigate(`/handel-magazyn/sprzedaz/${doc.id}`);
    },
    onError: (err) => {
      /*
       * Komunikat MUSI rozróżniać, kto odmówił. „Symfonia odrzuciła dokument" przy naszym
       * własnym błędzie wysyła użytkownika w złą stronę — szuka przyczyny w ERP, gdy problem
       * jest u nas. Trzy źródła, trzy różne treści i, co ważniejsze, zawsze POWÓD: bez niego
       * nie da się poprawić dokumentu.
       */
      const status = isAxiosError(err) ? err.response?.status : undefined;
      const data = isAxiosError(err)
        ? (err.response?.data as
            | {
                error?: string;
                message?: string;
                details?: { body?: string; fieldErrors?: Record<string, string[]> };
              }
            | undefined)
        : undefined;

      const symfoniaRefused = data?.error === 'SYMFONIA_UNAVAILABLE' || !!data?.details?.body;
      const fieldErrors = Object.entries(data?.details?.fieldErrors ?? {})
        .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
        .join(' · ');

      toast.show({
        variant: 'error',
        title: symfoniaRefused
          ? t('tenant.handelMagazyn.newSale.toast.failed')
          : t('tenant.handelMagazyn.newSale.toast.failedLocal'),
        description:
          data?.details?.body ??
          (fieldErrors || undefined) ??
          data?.message ??
          (status ? `HTTP ${status}` : undefined),
      });
    },
  });

  const canSubmit =
    !!companyId &&
    !!documentTypeId &&
    !!series &&
    !!departmentCode &&
    !!registryId &&
    !!paymentFormId &&
    rows.length > 0 &&
    rows.every((r) => r.productCode && num(r.quantity) > 0) &&
    !mutation.isPending;

  const sendsToKsef = Number(ksefKind) !== HM_KSEF_KINDS.NONE;

  if (!companyId) {
    return (
      <div className="p-4 text-sm text-text-secondary">{t('tenant.handelMagazyn.pickCompany')}</div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">
            {t('tenant.handelMagazyn.newSale.title')}
          </h1>
          <p className="text-sm text-text-secondary">
            {t('tenant.handelMagazyn.newSale.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            leftIcon={<Save className="h-4 w-4" />}
            disabled={!canSubmit}
            loading={mutation.isPending && !confirmIssue}
            onClick={() => mutation.mutate(false)}
          >
            {t('tenant.handelMagazyn.newSale.saveToBuffer')}
          </Button>
          <Button
            variant="primary"
            leftIcon={<Send className="h-4 w-4" />}
            disabled={!canSubmit}
            onClick={() => setConfirmIssue(true)}
          >
            {t('tenant.handelMagazyn.newSale.issue')}
          </Button>
        </div>
      </div>

      {dictionaries.error && (
        <div className="mb-3 rounded-md border border-border-primary bg-bg-secondary p-4 text-sm text-text-secondary">
          {t('tenant.handelMagazyn.problem.UNKNOWN')}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 rounded-md border border-border-primary bg-bg-secondary p-4 lg:grid-cols-2">
        <div className="space-y-3">
          <Select
            label={t('tenant.handelMagazyn.newSale.documentType')}
            value={documentTypeId}
            onChange={(e) => setDocumentTypeId(e.target.value)}
            placeholder={t('tenant.handelMagazyn.newSale.choose')}
            options={(dict?.documentTypes ?? []).map((d) => ({
              value: String(d.id),
              label: `${d.code} — ${d.name}`,
            }))}
          />
          <Select
            label={t('tenant.handelMagazyn.newSale.series')}
            value={series}
            onChange={(e) => setSeries(e.target.value)}
            disabled={!documentTypeId || seriesQuery.isLoading}
            placeholder={t('tenant.handelMagazyn.newSale.chooseAfterType')}
            options={(seriesQuery.data ?? []).map((s) => ({ value: s.code, label: s.code }))}
          />
          <Select
            label={t('tenant.handelMagazyn.newSale.department')}
            value={departmentCode}
            onChange={(e) => setDepartmentCode(e.target.value)}
            options={(dict?.departments ?? []).map((d) => ({
              value: d.code,
              label: d.name || d.code,
            }))}
          />
          <div>
            <span className="mb-1 block text-sm font-medium text-text-primary">
              {t('tenant.handelMagazyn.newSale.buyer')}
            </span>
            {/* Nabywca jednokrotny ZASTĘPUJE wybór z kartoteki — pokazujemy wtedy jego dane
                zamiast wyszukiwarki, żeby nie dawać dwóch aktywnych źródeł nabywcy naraz. */}
            {adHocBuyer ? (
              <div className="flex items-start justify-between gap-2 rounded-md border border-border-primary bg-bg-tertiary px-2.5 py-1.5">
                <div className="min-w-0">
                  <p className="truncate text-sm text-text-primary">{adHocBuyer.name}</p>
                  <p className="truncate text-xs text-text-secondary">
                    {[
                      adHocBuyer.taxId ? `NIP ${adHocBuyer.taxId}` : null,
                      [adHocBuyer.postCode, adHocBuyer.city].filter(Boolean).join(' '),
                      t('tenant.handelMagazyn.adHoc.badge'),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setAdHocOpen(true)}>
                    {t('common.edit')}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setAdHocBuyer(null)}>
                    {t('common.remove')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <div className="min-w-0 flex-1">
                  <SearchableSelect
                    value={buyerId}
                    onChange={setBuyerId}
                    allowClear
                    options={(contractors.data ?? []).map((c) => ({
                      value: String(c.id),
                      label: c.name,
                      description: [c.code, c.taxId ? `NIP ${c.taxId}` : null]
                        .filter(Boolean)
                        .join(' · '),
                    }))}
                    placeholder={t('tenant.handelMagazyn.newSale.buyerPlaceholder')}
                    data-testid="hm-new-buyer"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAdHocOpen(true)}
                  title={t('tenant.handelMagazyn.adHoc.open')}
                  aria-label={t('tenant.handelMagazyn.adHoc.open')}
                  className="h-9 w-9 shrink-0 p-0"
                  data-testid="hm-adhoc-open"
                >
                  <UserPlus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <Select
            label={t('tenant.handelMagazyn.newSale.paymentRegistry')}
            value={registryId}
            onChange={(e) => setRegistryId(e.target.value)}
            placeholder={t('tenant.handelMagazyn.newSale.choose')}
            options={(dict?.paymentRegistries ?? []).map((r) => ({
              value: String(r.id),
              label: r.name,
            }))}
          />
          <Select
            label={t('tenant.handelMagazyn.newSale.paymentForm')}
            value={paymentFormId}
            onChange={(e) => setPaymentFormId(e.target.value)}
            disabled={!registryId || formsQuery.isLoading}
            hint={t('tenant.handelMagazyn.newSale.paymentFormHint')}
            placeholder={t('tenant.handelMagazyn.newSale.chooseAfterRegistry')}
            options={(formsQuery.data ?? []).map((f) => ({ value: String(f.id), label: f.name }))}
          />
          <div className="grid grid-cols-3 gap-2">
            <label className="flex flex-col text-sm text-text-secondary">
              {t('tenant.handelMagazyn.columns.issueDate')}
              <DatePicker value={issueDate} onChange={(v) => setIssueDate(v || '')} />
            </label>
            <label className="flex flex-col text-sm text-text-secondary">
              {t('tenant.handelMagazyn.columns.saleDate')}
              <DatePicker value={saleDate} onChange={(v) => setSaleDate(v || '')} />
            </label>
            <label className="flex flex-col text-sm text-text-secondary">
              {t('tenant.handelMagazyn.columns.maturityDate')}
              <DatePicker value={maturityDate} onChange={(v) => setMaturityDate(v || '')} />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select
              label={t('tenant.handelMagazyn.newSale.priceType')}
              value={salePriceType}
              onChange={(e) => {
                const nextType = e.target.value;
                setSalePriceType(nextType);
                // Cennik niesie własny rodzaj ceny — podpowiadamy go, żeby nie wystawić
                // dokumentu netto na cenniku brutto. Użytkownik może to nadpisać.
                const picked = dict?.salePriceTypes.find((p) => String(p.type) === nextType);
                if (picked?.kind) setPriceKind(String(picked.kind));
                // Ceny PODSTAWIONE przeliczamy na nowy cennik; wpisane ręcznie zostają —
                // zmiana cennika nie może po cichu skasować pracy użytkownika.
                setRows((prev) =>
                  prev.map((row) => {
                    if (!row.priceFromCatalog || !row.productCode) return row;
                    const product = products.data?.find((p) => p.code === row.productCode);
                    const price = product ? catalogPrice(product, nextType) : null;
                    return price ? { ...row, unitPrice: price.value } : row;
                  }),
                );
              }}
              options={(dict?.salePriceTypes ?? []).map((p) => ({
                value: String(p.type),
                label: p.name,
              }))}
            />
            <Select
              label={t('tenant.handelMagazyn.newSale.priceKind')}
              value={priceKind}
              onChange={(e) => setPriceKind(e.target.value)}
              hint={t('tenant.handelMagazyn.newSale.priceKindHint')}
              options={[
                {
                  value: String(HM_PRICE_KINDS.NET),
                  label: t('tenant.handelMagazyn.newSale.priceKindNet'),
                },
                {
                  value: String(HM_PRICE_KINDS.GROSS),
                  label: t('tenant.handelMagazyn.newSale.priceKindGross'),
                },
              ]}
            />
          </div>
          <Select
            label={t('tenant.handelMagazyn.newSale.ksef')}
            value={ksefKind}
            onChange={(e) => setKsefKind(e.target.value)}
            hint={
              sendsToKsef
                ? t('tenant.handelMagazyn.newSale.ksefWarning')
                : t('tenant.handelMagazyn.newSale.ksefNoneHint')
            }
            options={[
              {
                value: String(HM_KSEF_KINDS.NONE),
                label: t('tenant.handelMagazyn.newSale.ksefNone'),
              },
              {
                value: String(HM_KSEF_KINDS.PAPER),
                label: t('tenant.handelMagazyn.newSale.ksefPaper'),
              },
              {
                value: String(HM_KSEF_KINDS.STRUCTURED_MANUAL),
                label: t('tenant.handelMagazyn.newSale.ksefManual'),
              },
              {
                value: String(HM_KSEF_KINDS.STRUCTURED_AUTO),
                label: t('tenant.handelMagazyn.newSale.ksefAuto'),
              },
            ]}
          />
        </div>
      </div>

      <div className="mt-4 rounded-md border border-border-primary bg-bg-secondary p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">
            {t('tenant.handelMagazyn.detail.tabs.positions')}
          </h2>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => setRows((prev) => [...prev, emptyRow()])}
          >
            {t('tenant.handelMagazyn.newSale.addPosition')}
          </Button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-border-primary text-left text-xs uppercase tracking-wide text-text-tertiary">
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.code')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.description')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {t('tenant.handelMagazyn.detail.pos.quantity')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.unit')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {/* Nagłówek MUSI mówić, którą cenę wpisujesz — ta sama liczba znaczy co innego
                      przy cenniku brutto i netto. */}
                  {Number(priceKind) === HM_PRICE_KINDS.GROSS
                    ? t('tenant.handelMagazyn.newSale.priceGrossColumn')
                    : t('tenant.handelMagazyn.newSale.priceNetColumn')}
                </th>
                <th className="px-2 py-1.5 font-medium">
                  {t('tenant.handelMagazyn.detail.pos.vat')}
                </th>
                <th className="px-2 py-1.5 text-right font-medium">
                  {Number(priceKind) === HM_PRICE_KINDS.GROSS
                    ? t('tenant.handelMagazyn.columns.gross')
                    : t('tenant.handelMagazyn.columns.net')}
                </th>
                <th className="w-10 px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, index) => (
                <tr key={r.key} className="border-b border-border-primary/60">
                  <td className="px-2 py-1.5" style={{ minWidth: 220 }}>
                    <SearchableSelect
                      value={r.productCode || null}
                      onChange={(code) => pickProduct(r.key, code)}
                      options={(products.data ?? []).map((p) => ({
                        value: p.code,
                        label: p.code,
                        description: p.name,
                      }))}
                      placeholder={t('tenant.handelMagazyn.newSale.productPlaceholder')}
                      data-testid={`hm-new-product-${index}`}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={r.name}
                      onChange={(e) => setRow(r.key, { name: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={r.quantity}
                      inputMode="decimal"
                      className="text-right"
                      onChange={(e) => setRow(r.key, { quantity: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={r.unit}
                      className="w-20"
                      onChange={(e) => setRow(r.key, { unit: e.target.value })}
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input
                      value={r.unitPrice}
                      inputMode="decimal"
                      className="text-right"
                      onChange={(e) =>
                        setRow(r.key, { unitPrice: e.target.value, priceFromCatalog: false })
                      }
                    />
                  </td>
                  <td className="px-2 py-1.5">
                    <Select
                      value={r.vatRateId}
                      onChange={(e) => setRow(r.key, { vatRateId: e.target.value })}
                      placeholder={t('tenant.handelMagazyn.newSale.vatDefault')}
                      options={(dict?.vatRates ?? []).map((v) => ({
                        value: String(v.id),
                        label: v.code,
                      }))}
                    />
                  </td>
                  <td className="px-2 py-1.5 text-right font-medium text-text-primary">
                    {money(num(r.quantity) * num(r.unitPrice))}
                  </td>
                  <td className="px-2 py-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('tenant.handelMagazyn.newSale.removePosition')}
                      disabled={rows.length === 1}
                      onClick={() => setRows((prev) => prev.filter((x) => x.key !== r.key))}
                      className="h-7 w-7 p-0"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <div className="space-y-2">
          <Textarea
            label={t('tenant.handelMagazyn.detail.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
          <Textarea
            label={t('tenant.handelMagazyn.detail.note')}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
          />
        </div>

        {/* Sumy podglądowe — autorytatywne kwoty wylicza Symfonia przy zapisie (patrz nagłówek). */}
        <div className="rounded-md border border-border-primary bg-bg-secondary p-3">
          <p className="mb-2 text-xs text-text-tertiary">
            {t('tenant.handelMagazyn.newSale.totalsPreview')}
          </p>
          <dl className="space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-text-secondary">{t('tenant.handelMagazyn.columns.net')}</dt>
              <dd className="font-medium text-text-primary">{money(totals.net)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-text-secondary">{t('tenant.handelMagazyn.columns.vat')}</dt>
              <dd className="font-medium text-text-primary">{money(totals.vat)}</dd>
            </div>
            <div className="flex justify-between border-t border-border-primary pt-1">
              <dt className="text-text-secondary">{t('tenant.handelMagazyn.columns.gross')}</dt>
              <dd className="font-semibold text-text-primary">{money(totals.gross)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {adHocOpen && (
        <AdHocBuyerModal
          open={adHocOpen}
          initial={adHocBuyer}
          onClose={() => setAdHocOpen(false)}
          onSave={(buyer) => {
            setAdHocBuyer(buyer);
            // Wybór z kartoteki przestaje obowiązywać — dokument ma jednego nabywcę.
            setBuyerId(null);
            setAdHocOpen(false);
          }}
        />
      )}

      <ConfirmDialog
        open={confirmIssue}
        onClose={() => setConfirmIssue(false)}
        onConfirm={() => {
          setConfirmIssue(false);
          mutation.mutate(true);
        }}
        /* Nie `danger`: wystawienie nie NISZCZY danych, tylko tworzy dokument, którego nie da się
           cofnąć. Czerwony przycisk „Usuń" byłby kłamstwem o rodzaju ryzyka — ostrzeżenie niesie
           treść komunikatu, nie kolor. */
        variant="primary"
        confirming={mutation.isPending}
        title={t('tenant.handelMagazyn.newSale.confirmTitle')}
        message={
          sendsToKsef
            ? t('tenant.handelMagazyn.newSale.confirmKsef')
            : t('tenant.handelMagazyn.newSale.confirmBody')
        }
        confirmLabel={t('tenant.handelMagazyn.newSale.issue')}
      />
    </div>
  );
}

export default HmSaleNew;
